import {
    ExclamationCircleOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import {
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Pagination,
    Popconfirm,
    Radio,
    Row,
    Select,
    Space,
    Table,
    Tooltip,
    Typography
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import { PrimaryTooltip } from 'components/index';
import React, {
    useContext,
    useEffect,
    useRef
} from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import API_SERVICE from 'services/api-service';
import { CommonLinkProjects } from 'views/Claims/CommonLinkProjects';
import { directCostModalContent, indirectCostModalContent } from '../directCostGuidance';

const { Text } = Typography;
const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
    key: string;
    name: string;
    age: string;
    address: string;
    isDirectCost: boolean | null;
}

interface EditableRowProps {
    index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

interface EditableCellProps {
    title: React.ReactNode;
    editable: boolean;
    children: React.ReactNode;
    dataIndex: keyof Item;
    record: Item;
    handleSave: (record: Item) => void;
    inputType: 'number' | 'text' | 'select' | 'radio' | 'percentage';
    options: Array<any>;
    claimDetails: any;
    componentType?: 'soft' | 'cms';
}

const getFieldRules = (dataIndex: string, componentType?: string) => {
    switch (dataIndex) {
        case 'name':
            return [
                { required: true, message: 'Name required.' },
                { max: 255, message: 'Max 255 chars.' },
            ];
        case 'consumableId':
        case 'softwareConsumableId':
            return [{ required: true, message: 'Type required.' }];
        case 'description':
            return [
                { required: true, message: 'Description required.' },
                { min: 50, message: 'Min 50 characters.' },
                { max: 500, message: 'Max 500 characters.' },
            ];
        case 'netAmount':
            return [
                { required: true, message: 'Amount required.' },
                { type: 'number' as const, min: 1, message: 'Min £1.' },
                { type: 'number' as const, max: 9999999.99, message: 'Max £9,999,999.' },
            ];
        case 'rndAllocationPercentage':
            return [{ required: true, message: 'R&D % required.' }];
        case 'smeAllocation':
            return [{ required: true, message: 'SME % required.' }];
        case 'isDirectCost':
            return [{ required: true, message: 'Selection required.' }];
        default:
            return [{ required: true, message: 'Required.' }];
    }
};

const getNumberInputProps = (dataIndex: string) => {
    if (dataIndex === 'netAmount') return { min: 1, max: 9999999.99 };
    return { min: 0, max: 9999999.99 };
};

const getTextMaxLength = (dataIndex: string): number | undefined => {
    if (dataIndex === 'name') return 255;
    if (dataIndex === 'description') return 500;
    return undefined;
};

const EditableCell: React.FC<EditableCellProps> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    inputType,
    options,
    claimDetails,
    componentType,
    ...restProps
}) => {
    const inputRef = useRef<any>(null);
    const form = useContext(EditableContext)!;

    //@ts-ignore
    useEffect(() => {
        form.setFieldsValue({ [dataIndex]: record?.[dataIndex] });
    }, [record?.[dataIndex]]);

    const handleIsConnectedChange = async (event: any) => {
        if (event.target.value) {
            form.setFieldsValue({ adjustmentsPercentage: 100 });
        } else {
            form.setFieldsValue({ adjustmentsPercentage: 65 });
        }
        save();
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            const netAmount = Number(values.netAmount) || 0;
            const rndAllocationPercentage = Number(values.rndAllocationPercentage) || 0;
            const total = (rndAllocationPercentage / 100 * netAmount).toFixed(2);
            // Add the calculated total to values.values object
            values.total = total;


            // Get claim type ID from claim details
            const claimTypeId = claimDetails?.typeId;
            
            // Calculate amounts based on claim type
            if (claimTypeId === 3) { // MIXED type
                const smeAllocation = Number(values.smeAllocation) || 0;
                const rdecAllocation = 100 - smeAllocation;
                const totalAmount = Number(total) || 0;
                values.smeTotal = ((smeAllocation / 100) * totalAmount).toFixed(2);
                values.rdecAllocation = rdecAllocation;
                values.rdecTotal = ((rdecAllocation / 100) * totalAmount).toFixed(2);
            } else if (claimTypeId === 2) { // SME type
                values.smeAllocation = 100;
                values.smeTotal = total;
                values.rdecAllocation = 100;
                values.rdecTotal = 0;
            } else if (claimTypeId === 1) { // RDEC type
                values.rdecAllocation = 100;
                values.rdecTotal = total;
                values.smeAllocation = 0;
                values.smeTotal = 0;
            } else if (claimTypeId === 4) { // MERGED type
                values.smeTotal = 0;
                values.rdecAllocation = 0;
                values.rdecTotal = 0;
                values.smeAllocation = 0;
            }

            
            // toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        } 
    };

    let childNode = children;

    if (editable) {
        childNode = (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Form.Item
                    style={{ margin: 0, flex: 1, minWidth: 0 }}
                    name={dataIndex}
                    rules={getFieldRules(dataIndex as string, componentType)}
                >
                    {inputType === 'radio' ? (
                        <Radio.Group
                            onChange={(e) => {
                                if (dataIndex === 'isDirectCost') {
                                    Modal.info({
                                        title: e.target.value
                                            ? 'Direct Cost Guidance'
                                            : 'Indirect Qualifying Activities',
                                        content: e.target.value
                                            ? directCostModalContent
                                            : indirectCostModalContent,
                                        centered: true,
                                        width: 720,
                                    });
                                }
                                handleIsConnectedChange(e)
                            }}
                            ref={inputRef}
                        >
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    ) : inputType === 'select' ? (
                        <Select ref={inputRef} onSelect={save} onBlur={() => setTimeout(save, 0)}>
                            {options.map((item, i) => (
                                <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                </Select.Option>
                            ))}
                        </Select>
                    ) : inputType === 'number' ? (
                        <InputNumber
                            {...getNumberInputProps(dataIndex as string)}
                            formatter={(value) =>
                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            //@ts-ignore
                            parser={(value) => value.replace(/\£\s?|(,*)/g, '')}
                            //@ts-ignore
                            addonBefore={'£'}
                            ref={inputRef}
                            onPressEnter={save}
                            onBlur={() => setTimeout(save, 0)}
                        />
                    ) : inputType === 'percentage' ? (
                        <InputNumber
                            min={0}
                            max={100}
                            formatter={(value) => `${value}`}
                            //@ts-ignore
                            parser={(value) => value.replace('%', '')}
                            //@ts-ignore
                            addonAfter={'%'}
                            ref={inputRef}
                            onPressEnter={save}
                            onBlur={() => setTimeout(save, 0)}
                        />
                    ) : (
                        <Input
                            ref={inputRef}
                            maxLength={getTextMaxLength(dataIndex as string)}
                            onPressEnter={save}
                            onBlur={() => setTimeout(save, 0)}
                        />
                    )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                    {() => {
                        const errors = form.getFieldError(dataIndex as string);
                        if (!errors.length) return null;
                        return (
                            <Tooltip title={errors[0]} color="#ff4d4f" placement="top">
                                <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 14, flexShrink: 0, cursor: 'pointer' }} />
                            </Tooltip>
                        );
                    }}
                </Form.Item>
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

interface RouteParams {
    claimId: string;
}

type EditableTableProps = Parameters<typeof Table>[0] & RouteComponentProps<RouteParams> & {
    type?: 'soft' | 'cms';
};

interface DataType {
    key: React.Key;
    id: null;
    name: string;
    consumableId: string;
    description: string;
    netAmount: string;
    rndAllocationPercentage: string;
    total: string;
    isDirectCost: boolean | null;
    projects: Array<any>;
}

interface EditableTableState {
    compareCount: number;
    dataSource: DataType[];
    count: number;
    fetching: boolean;
    total: number;
    pagination: { page: number; perpage: any };
    financialDetails: Array<any>;
    claimId: any;
    updateData: Array<any>;
    saving: boolean;
    claimDetails: any;
    columns: any;
    consumablesTypeList: Array<any>;
    consumablesCostTotal: { netAmount: any; rnd: any; total: any };
    isProjectModalVisible: boolean;
    currentRecord: any;
    projectsList: Array<any>;
    openLinkProjectsModal: boolean;
    record: any;
    index: any;
}

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>;

class Consumables extends React.Component<
    EditableTableProps,
    EditableTableState
> {
    columns: (ColumnTypes[number] & {
        editable?: boolean;
        dataIndex: string;
    })[];

    constructor(props: EditableTableProps) {
        super(props);

        this.columns = [
            {
                title: '',
                dataIndex: 'del',
                width: '50px',
                //@ts-ignore
                render: (_, record: { key: React.Key }) =>
                    this.state.dataSource.length >= 1 ? (
                        <Popconfirm
                            title="Sure to delete?"
                            onConfirm={() => this.handleDelete(record)}
                        >
                            <div className="text-primary cursor-pointer text-center">
                                <MinusCircleOutlined />
                            </div>
                        </Popconfirm>
                    ) : null,
            },
            {
                title: 'S.No',
                dataIndex: 'sNO',
                width: '70px',
                render: (a: any, v: any, index: number) => (
                    <Text>
                        {(this.state.pagination.page - 1) *
                            this.state.pagination.perpage +
                            index +
                            1}
                    </Text>
                ),
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text={`${this.props.type === 'soft' ? 'Software Consumable' : 'Consumable'} Name`}
                    />
                ),
                dataIndex: 'name',
                width: '200px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text={`${this.props.type === 'soft' ? 'Software Consumable' : 'Consumable'} Type`}
                    />
                ),
                dataIndex: props.type === 'soft' ? 'softwareConsumableId' : 'consumableId',
                width: '200px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip title="Min 50 characters, max 500 characters." text="Description" />
                ),
                dataIndex: 'description',
                width: '180px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Min £1, max £9,999,999.99"
                        text="Net Amount (GBP)"
                    />
                ),
                dataIndex: 'netAmount',
                width: '180px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Enter a value between 0% and 100%"
                        text="R&D Allocation (%)"
                    />
                ),
                dataIndex: 'rndAllocationPercentage',
                width: '150px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip title="prompt text" text="Total (GBP)" />
                ),
                dataIndex: '',
                width: '150px',
                editable: false,
                render: (a: any) => {
                    const netAmount = Number(a.netAmount) || 0;
                    const rndAllocationPercentage = Number(a.rndAllocationPercentage) || 0;
                    const total = (rndAllocationPercentage / 100 * netAmount).toFixed(2);
                    return (
                        <Text>
                            {`£ ${total}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Text>
                    );
                }
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Is Direct Cost?"
                        text="Is Direct Cost?"
                    />
                ),
                dataIndex: 'isDirectCost',
                width: '150px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Link Projects"
                        text="Link Projects"
                    />
                ),
                dataIndex: '',
                width: '150px',
                render: (a: any, record: any, index: number) => (
                    <Button
                        type="primary"
                        disabled={(
                            a.rndAllocationPercentage / 100 * a.netAmount
                        ) <= 0}
                        size="small"
                        onClick={() => this.showProjectsModal(record, index)}
                    >
                        Link Projects
                    </Button>
                )
            },
        ];

        this.state = {
            compareCount: 0,
            dataSource: [],
            count: 0,
            fetching: false,
            total: 10,
            pagination: { page: 1, perpage: '10' },
            financialDetails: [],
            claimId: null,
            updateData: [],
            saving: false,
            columns: this.columns,
            claimDetails: {},
            consumablesTypeList: [],
            consumablesCostTotal: { netAmount: '', rnd: '', total: '' },
            isProjectModalVisible: false,
            currentRecord: null,
             projectsList: [],
            openLinkProjectsModal: false,
            record: null,
            index: null,
        };
    }

    handleProjectModalCancel = () => {
        this.setState({
            isProjectModalVisible: false,
            currentRecord: null
        });
    }

    showProjectsModal = (record: any,index:number) => {
        this.setState({
            openLinkProjectsModal: true,
            record: record,
            index: index
        });
    };


    componentDidMount(): void {
        const {
            // @ts-ignore
            match: { params },
        } = this.props;
        this.getConsumablesTypeList();
        this.getProjects(params.claimId);
        this.setState({ claimId: params.claimId }, () => {
            this.getConsumablesCostList();
        });
    }

    setUpdatedColumns = () => {
        const editableSMEColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (%)"
                    />
                ),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (GBP)"
                    />
                ),
                dataIndex: 'smeTotal',
                width: '200px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? Number(a).toFixed(2) : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];

        const nonEditableSMEColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (%)"
                    />
                ),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: false,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (GBP)"
                    />
                ),
                dataIndex: 'smeTotal',
                width: '200px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? Number(a).toFixed(2) : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];

        const nonEditableRDECColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="RDEC Allocation (%)"
                    />
                ),
                key: 'rdecAllocation',
                width: '150px',
                editable: false,
                render: (a: any) => (
                    <Text>{`${
                        100 - (a.smeAllocation ? a.smeAllocation : 0)
                    } %`}</Text>
                ),
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="RDEC Allocation (GBP)"
                    />
                ),
                dataIndex: 'rdecTotal',
                width: '150px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? Number(a).toFixed(2) : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];
        if (this.state.claimDetails?.type?.id === 1) {
            let newColumns = [...this.columns, ...nonEditableRDECColumn];
            this.setState({ columns: newColumns });
        } else if (this.state.claimDetails?.type?.id === 2) {
            let newColumns = [...this.columns, ...nonEditableSMEColumn];
            this.setState({ columns: newColumns });
        } else if (this.state.claimDetails?.type?.id === 3) {
            let newColumns = [
                ...this.columns,
                ...editableSMEColumn,
                ...nonEditableRDECColumn,
            ]; // @media screen and (min-width: 780px) {
            //     .addEmployeeBox {
            //         display: flex;
            //         flex-direction: column;
            //     }
            // }

            this.setState({ columns: newColumns });
        }
    };

    handleDelete = (row: any) => {
        if (row.id) {
            this.handleDeleteConsumablesCost(row.id);
        } else {
            const dataSource = [...this.state.dataSource];
            this.setState({
                dataSource: dataSource.filter((item) => item.key !== row.key),
            });
        }
    };

    handleAdd = () => {
        const { count, dataSource } = this.state;
        const newData: DataType = {
            key: count,
            id: null,
            name: '',
            consumableId: '',
            description: '',
            netAmount: '',
            rndAllocationPercentage: '',
            total: '',
            isDirectCost: null, 
            projects: [],
        };
        this.setState({
            dataSource: [...dataSource, newData],
            count: count + 1,
        });
    };

    handleSave = (row: DataType) => {
        //to add new dummy row to frontend list
        const newData = [...this.state.dataSource];
        const index = newData.findIndex((item) => row.key === item.key);
        const item = newData[index];
        newData.splice(index, 1, {
            ...item,
            ...row,
        });

        //to update the reqObj for create/update
        const updateData: Array<any> = [...this.state.updateData];
        const isExist = updateData.findIndex((item) => row.key === item.key);
        if (isExist > -1) {
            const updateItem = updateData[isExist];
            updateData.splice(isExist, 1, {
                ...updateItem,
                ...row,
            });
        } else {
            updateData.push(row);
        }

                this.setState({ dataSource: newData, updateData });

    };

    getConsumablesTypeList = async () => {
        try {
            if (this.props.type === 'soft') {
                await API_SERVICE.softwareConsumablesTypeList().then(
                    (result) => {
                        const {
                            data: { data },
                        } = result;
                        this.setState({ consumablesTypeList: data.items });
                    },
                );
                return;
            }
            await API_SERVICE.consumablesTypeList().then((result) => {
                const {
                    data: { data },
                } = result;
                this.setState({ consumablesTypeList: data.items });
            });
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    getConsumablesCostList = async () => {
        this.setState({ fetching: true });
        const params = { ...this.state.pagination };
        try {
            
            const {
                data: { data },
            } = this.props.type ==='soft'? await API_SERVICE.softwareConsumablesCostList(
                this.state.claimId,
                params,
            ) : await API_SERVICE.consumablesCostList(
                this.state.claimId,
                params,
            );
            let mData = data.items.map((item: any, i: number) => {
                item.key = i;
                return item;
            });
            this.setState({
                dataSource: mData,
                count: data.items.length,
                total: data.total,
            });
            this.getConsumablesCostTotal();
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        } finally {
            this.setState({ fetching: false });
        }
    };

    getClaimsDetails = async () => {
        try {
            const {
                data: { data },
            } = await API_SERVICE.getClaimById(this.state.claimId);
            this.setState({ claimDetails: data }, () => {
                console.log(data);
                this.setUpdatedColumns();
            });
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    getConsumablesCostTotal = async () => {
        try {
            const endpoint = this.props.type === 'soft' 
                ? API_SERVICE.softwareConsumablesCostTotal 
                : API_SERVICE.consumablesCostTotal;
                
            const { data: { data } } = await endpoint(this.state.claimId);
            this.setState({ consumablesCostTotal: data });
            await this.getClaimsDetails();
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    getProjects = async (claimId: string) => {
        try {
            const {
                data: { data },
            } = await API_SERVICE.getProjects(claimId);
            this.setState({ projectsList: data.items });
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };


    handleSaveConsumablesCost = async () => {
    let reqObj = {} as any;
    reqObj['costs'] = this.state.updateData;
    if (
        this.state.dataSource.length > this.state.compareCount &&
        reqObj['costs'].length === 0
    ) {
        notification.error({
            message: `Please fill all required fields before saving.`,
        });
        return;
    }
    if (
        this.state.dataSource.length === this.state.compareCount &&
        reqObj['costs'].length === 0
    ) {
        notification.success({
            message: `Details Already Saved`,
        });
        return;
    }

    const typeKey = this.props.type === 'soft' ? 'softwareConsumableId' : 'consumableId';
    const invalidRow = this.state.updateData.find((row: any) =>
        !row.name ||
        !row[typeKey] ||
        !row.description ||
        String(row.description).length < 50 ||
        !row.netAmount ||
        row.rndAllocationPercentage === '' || row.rndAllocationPercentage === null || row.rndAllocationPercentage === undefined
    );
    if (invalidRow) {
        notification.error({
            message: 'Please fill all required fields. Description must be at least 50 characters.',
        });
        return;
    }
    this.setState({ saving: true });

    try {
        if (this.props.type === 'soft') {
            await API_SERVICE.softwareCostCreate(this.state.claimId, reqObj);
        } else {
            await API_SERVICE.consumablesCostCreate(this.state.claimId, reqObj);
        }
        
        this.getConsumablesCostList();
        this.setState({ updateData: [] });
        notification.success({
            message: 'Success',
        });
    } catch (e) {
        console.log(e);
        notification.error({ message: API_SERVICE.handleErrors(e) });
    } finally {
        this.setState({ saving: false });
    }
    };

    handleDeleteConsumablesCost = async (id: any) => {
        this.setState({ saving: true });
        try {
            const endpoint = this.props.type === 'soft' 
                ? API_SERVICE.softwareConsumablesCostDelete 
                : API_SERVICE.consumablesCostDelete;
                
            await endpoint(this.state.claimId, id);
            this.getConsumablesCostList();
            notification.success({
                message: 'Success',
            });
        } catch (e) {
            console.log(e);
            notification.error({ message: API_SERVICE.handleErrors(e) });
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const { dataSource } = this.state;
        const components = {
            body: {
                row: EditableRow,
                cell: (props: any) => (
                    <EditableCell
                        {...props}
                        claimDetails={this.state.claimDetails}
                    />
                ),
            },
        };
        //@ts-ignore
        const columns = this.state.columns.map((col) => {
            if (!col.editable) {
                return col;
            }
            return {
                ...col,
                onCell: (record: DataType) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave: this.handleSave,
                    inputType:
                    col.dataIndex === 'isDirectCost' ? 'radio' :
                        col.dataIndex === 'consumableId' || col.dataIndex === 'softwareConsumableId'
                            ? 'select'
                            : col.dataIndex === 'netAmount' ||
                              col.dataIndex === 'total'
                            ? 'number'
                            : col.dataIndex === 'rndAllocationPercentage' ||
                              col.dataIndex === 'smeAllocation' ||
                              col.dataIndex === 'rdecAllocation'
                            ? 'percentage'
                            : 'text',
                    options: this.state.consumablesTypeList,
                    componentType: this.props.type,
                }),
            };
        });
        return (
            <>
                <Row
                    className={`${
                        this.state.claimDetails.stageId >= 2 &&
                        'cursor-not-allowed'
                    }`}
                >
                    <Table
                        loading={this.state.fetching}
                        scroll={{ x: true, y: 'calc(100vh - 280px)' }}
                        components={components}
                        rowClassName={() =>
                            `${
                                this.state.claimDetails.stageId >= 2 &&
                                'pointer-none'
                            } editable-row`
                        }
                        bordered
                        dataSource={dataSource}
                        columns={columns as ColumnTypes}
                        pagination={false}
                    />
                </Row>
                <div className="d-flex align-items-center justify-content-between my-3 addEmployeeBox">
                    <Button
                        disabled={this.state.claimDetails.stageId >= 2}
                        className="black-button"
                        onClick={this.handleAdd}
                        icon={<PlusOutlined />}
                    >
                        Add Consumable
                    </Button>
                    <div className="d-flex align-items-center">
                        <Space>
                            <Form.Item
                                className="mb-0"
                                style={{ width: '220px' }}
                            >
                                <Input
                                    disabled
                                    value={`Net Amount: ${
                                        this.state.consumablesCostTotal
                                            .netAmount &&
                                        this.state.consumablesCostTotal.netAmount.toFixed(
                                            2,
                                        )
                                    } GBP`}
                                />
                            </Form.Item>
                            {/*<Form.Item className="mb-0" style={{width: '220px'}}>
                                <Input disabled value={`${this.state.consumablesCostTotal.rnd} GBP`} />
                            </Form.Item>*/}
                            <Form.Item
                                className="mb-0"
                                style={{ width: '220px' }}
                            >
                                <Input
                                    disabled
                                    value={`Total: ${
                                        this.state.consumablesCostTotal.total &&
                                        this.state.consumablesCostTotal.total.toFixed(
                                            2,
                                        )
                                    } GBP`}
                                />
                            </Form.Item>
                        </Space>
                    </div>
                </div>
                <Row>
                    <Col xs={24} className={'linear justify-content-between'}>
                        <Pagination
                            showSizeChanger
                            showQuickJumper
                            defaultCurrent={1}
                            total={this.state.total}
                            onChange={(page, size) => {
                                this.setState(
                                    {
                                        pagination: {
                                            page: page,
                                            perpage: size,
                                        },
                                    },
                                    this.getConsumablesCostList,
                                );
                            }}
                        />

                        <Button
                            disabled={
                                this.state.claimDetails.stageId >= 2 ||
                                this.state.saving
                            }
                            loading={this.state.saving}
                            type={'primary'}
                            shape="round"
                            className="theme-button primary"
                            onClick={this.handleSaveConsumablesCost}
                            icon={<SaveOutlined />}
                        >
                            Save
                        </Button>
                    </Col>
                </Row>
                {this.state.openLinkProjectsModal &&
                    <CommonLinkProjects
                        displayOnly={false}
                        record={this.state.record}
                        index={this.state.index}
                        visible={this.state.openLinkProjectsModal}
                        onClose={() => this.setState({ openLinkProjectsModal: false })}
                        setState={(state: any) => this.setState(state)}
                        claimId={this.state.claimId}
                        projectsList={this.state.projectsList!}
                        handleSave={this.handleSave as unknown as (data: any) => Promise<void>}
                        type='consumables'
                    />
                }
            </>
        );
    }
}

// @ts-ignore
export default withRouter(Consumables);
// ReactDOM.render(<EditableTable />, mountNode);
